import moment from "moment";
import { useMemo, useState } from "react";
import {
  AssessmentGroupAnonymity,
  AssessmentGroupProviders,
  AssessmentType,
  ComplianceProgramAppliesTo,
  ComplianceProgramListItemFragment,
  ComplianceProgramRecurrence,
  GetComplianceProgramQuery,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import useLabel, { UseLabelType } from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import { getAssessmentTypeLabel } from "@helpers/constants";
import { assertEdgesNonNull } from "@helpers/helpers";

import ComplianceProgramRecurrenceTooltip from "./compliance-program-recurrence-tooltip";

const appliesToLabels = (label: UseLabelType) => ({
  [ComplianceProgramAppliesTo.Organization]: "All employees",
  [ComplianceProgramAppliesTo.Departments]: `All employees in specific ${label(
    "team",
    {
      pluralize: true,
    },
  )}`,
  [ComplianceProgramAppliesTo.ReportsTo]:
    "All employees that report to specific managers",
  [ComplianceProgramAppliesTo.Managers]: "All managers",
  [ComplianceProgramAppliesTo.Ics]: "All individual contributors (ICs)",
  [ComplianceProgramAppliesTo.Users]: "Specific employees",
  [ComplianceProgramAppliesTo.CreatorDirectReports]: "My direct reports",
  [ComplianceProgramAppliesTo.CreatorManagementTree]: "My reporting tree",
});

const getAppliesToLabel = (
  label: UseLabelType,
  appliesTo: ComplianceProgramAppliesTo,
  creator?: BasicUser | null,
  currentUserId?: number,
) => {
  if (appliesTo === ComplianceProgramAppliesTo.CreatorDirectReports) {
    return `${
      creator?.id === currentUserId ? "All my" : `All of ${creator?.name}'s`
    } direct reports`;
  }
  if (appliesTo === ComplianceProgramAppliesTo.CreatorManagementTree) {
    return `${
      creator?.id === currentUserId ? "All my" : `All of ${creator?.name}'s`
    } reports (full reporting tree)`;
  }
  return appliesToLabels(label)[appliesTo];
};

const maxUsersToList = 5;

const AppliesToUsersList = ({
  users,
}: {
  users:
    | NonNullable<
        GetComplianceProgramQuery["complianceProgram"]
      >["appliesToUsers"]
    | NonNullable<
        GetComplianceProgramQuery["complianceProgram"]
      >["appliesToManagers"];
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const userNodes = assertEdgesNonNull(users);
  return (
    <>
      {isOpen && (
        <Modal alignedTop onClose={() => setIsOpen(false)}>
          <div className="flex flex-col gap-6 p-6">
            <ModalTitle onClose={() => setIsOpen(false)}>Applies to</ModalTitle>
            <div className="flex flex-col gap-2">
              {userNodes.map((user) => (
                <div key={user.id} className="flex items-center gap-2">
                  <Avatar user={user} size={5} />
                  {user.name}
                </div>
              ))}
            </div>
          </div>
        </Modal>
      )}
      (
      {userNodes
        .slice(0, maxUsersToList)
        .map((user) => user.name)
        .join(", ")}
      {userNodes.length > maxUsersToList && (
        <>
          {" "}
          and{" "}
          {userNodes.length - 1 === maxUsersToList ? (
            userNodes[userNodes.length - 1].name
          ) : (
            <button onClick={() => setIsOpen(true)} className="hover:underline">
              {userNodes.length - maxUsersToList} more
            </button>
          )}
        </>
      )}
      )
    </>
  );
};

const ComplianceProgramReportingDetailsLabel = ({
  children,
}: {
  children: string;
}) => <div className="w-56 shrink-0 font-medium text-gray-500">{children}</div>;

const ComplianceProgramReportingAssessmentTypeDetails = ({
  assessmentTemplate,
}: {
  assessmentTemplate:
    | NonNullable<
        GetComplianceProgramQuery["complianceProgram"]
      >["peerAssessmentTemplate"]
    | NonNullable<
        GetComplianceProgramQuery["complianceProgram"]
      >["managerAssessmentTemplate"]
    | NonNullable<
        GetComplianceProgramQuery["complianceProgram"]
      >["performanceAssessmentTemplate"]
    | NonNullable<
        GetComplianceProgramQuery["complianceProgram"]
      >["engagementSurveyTemplate"];
}) => {
  if (!assessmentTemplate) return <div>N/A</div>;
  const labels = [];
  if (assessmentTemplate.anonymity === AssessmentGroupAnonymity.Anonymous) {
    labels.push("Anonymous");
  }
  if (
    assessmentTemplate.anonymity === AssessmentGroupAnonymity.NotAnonymous &&
    assessmentTemplate.assessmentType !== AssessmentType.Performance
  ) {
    labels.push("Not anonymous");
  }
  if (assessmentTemplate.anonymity === AssessmentGroupAnonymity.SemiAnonymous) {
    labels.push("Semi-anonymous");
  }
  if (assessmentTemplate.assessmentType !== AssessmentType.EngagementSurvey) {
    if (
      assessmentTemplate.providers === AssessmentGroupProviders.ManagerSelect
    ) {
      labels.push("Manager selects nominees");
    }
    if (
      assessmentTemplate.providers === AssessmentGroupProviders.SubjectSelect
    ) {
      labels.push("Subject selects nominees");
    }
  }
  return (
    <div>{`Enabled${labels.length > 0 ? ": " : ""}${labels.join(", ")}`}</div>
  );
};

const ComplianceProgramReportingDetails = ({
  complianceProgram,
}: {
  complianceProgram:
    | NonNullable<GetComplianceProgramQuery["complianceProgram"]>
    | ComplianceProgramListItemFragment;
}) => {
  const label = useLabel();
  const currentUser = currentUserVar();

  const performanceAssessmentTemplate = useMemo(
    () =>
      complianceProgram?.performanceAssessmentTemplate
        ? complianceProgram.performanceAssessmentTemplate
        : null,
    [complianceProgram],
  );
  const managerAssessmentTemplate = useMemo(
    () =>
      complianceProgram?.managerAssessmentTemplate
        ? complianceProgram.managerAssessmentTemplate
        : null,
    [complianceProgram],
  );
  const peerAssessmentTemplate = useMemo(
    () =>
      complianceProgram?.peerAssessmentTemplate
        ? complianceProgram.peerAssessmentTemplate
        : null,
    [complianceProgram],
  );
  const engagementSurveyTemplate = useMemo(
    () =>
      complianceProgram?.engagementSurveyTemplate
        ? complianceProgram.engagementSurveyTemplate
        : null,
    [complianceProgram],
  );
  const topicTemplate = useMemo(
    () =>
      complianceProgram?.requiredTopicTemplates
        ? assertEdgesNonNull(complianceProgram.requiredTopicTemplates)[0]
        : null,
    [complianceProgram],
  );

  const excludedUsers = assertEdgesNonNull(complianceProgram.excludedUsers);

  return (
    <div className="flex flex-col text-sm gap-2 text-gray-700">
      {!complianceProgram.ongoing && (
        <>
          <div className="flex items-center gap-2">
            <ComplianceProgramReportingDetailsLabel>
              Start date:
            </ComplianceProgramReportingDetailsLabel>
            <div>
              {moment(complianceProgram.startDate).format("MMM D, YYYY")}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ComplianceProgramReportingDetailsLabel>
              Due date:
            </ComplianceProgramReportingDetailsLabel>
            <div className="flex items-center gap-2">
              {moment(complianceProgram.dueDate).format("MMM D, YYYY")}
              {complianceProgram.recurrence !==
                ComplianceProgramRecurrence.None && (
                <ComplianceProgramRecurrenceTooltip
                  recurrence={complianceProgram.recurrence}
                />
              )}
            </div>
          </div>
          {!engagementSurveyTemplate && (
            <div className="flex items-center gap-2">
              <ComplianceProgramReportingDetailsLabel>
                Program period:
              </ComplianceProgramReportingDetailsLabel>
              <div className="flex items-center gap-2">
                {complianceProgram.periodStartDate &&
                complianceProgram.periodEndDate
                  ? `${moment(complianceProgram.periodStartDate).format(
                      "MMM D, YYYY",
                    )} - ${moment(complianceProgram.periodEndDate).format(
                      "MMM D, YYYY",
                    )}`
                  : "Not set"}
              </div>
            </div>
          )}
        </>
      )}
      {complianceProgram.ongoing && (
        <>
          <div className="flex items-center gap-2">
            <ComplianceProgramReportingDetailsLabel>
              Starts (days after start date):
            </ComplianceProgramReportingDetailsLabel>
            <div>{complianceProgram.startDateOffset}</div>
          </div>
          <div className="flex items-center gap-2">
            <ComplianceProgramReportingDetailsLabel>
              Due (days after start date):
            </ComplianceProgramReportingDetailsLabel>
            <div>{complianceProgram.startDateDueDateOffset}</div>
          </div>
          {!engagementSurveyTemplate && (
            <div className="flex items-center gap-2">
              <ComplianceProgramReportingDetailsLabel>
                Program period:
              </ComplianceProgramReportingDetailsLabel>
              <div>
                {complianceProgram.startDatePeriodStartOffset} -{" "}
                {complianceProgram.startDatePeriodEndOffset} days after start
                date
              </div>
            </div>
          )}
        </>
      )}

      {topicTemplate && (
        <div className="flex items-center gap-2">
          <ComplianceProgramReportingDetailsLabel>
            1-on-1 meeting:
          </ComplianceProgramReportingDetailsLabel>
          <div>{topicTemplate.title}</div>
        </div>
      )}
      {performanceAssessmentTemplate && (
        <>
          <div className="flex items-center gap-2">
            <ComplianceProgramReportingDetailsLabel>
              {`Self ${label("review", { capitalize: true })}: `}
            </ComplianceProgramReportingDetailsLabel>
            <ComplianceProgramReportingAssessmentTypeDetails
              assessmentTemplate={performanceAssessmentTemplate}
            />
          </div>
          <div className="flex items-center gap-2">
            <ComplianceProgramReportingDetailsLabel>
              {`Downward ${label("review", { capitalize: true })}: `}
            </ComplianceProgramReportingDetailsLabel>
            <ComplianceProgramReportingAssessmentTypeDetails
              assessmentTemplate={performanceAssessmentTemplate}
            />
          </div>
        </>
      )}
      {managerAssessmentTemplate && (
        <div className="flex items-center gap-2">
          <ComplianceProgramReportingDetailsLabel>
            {`${getAssessmentTypeLabel(AssessmentType.Manager)} ${label(
              "review",
              {
                capitalize: true,
              },
            )}: `}
          </ComplianceProgramReportingDetailsLabel>

          <ComplianceProgramReportingAssessmentTypeDetails
            assessmentTemplate={managerAssessmentTemplate}
          />
        </div>
      )}
      {peerAssessmentTemplate && (
        <div className="flex items-center gap-2">
          <ComplianceProgramReportingDetailsLabel>
            {`${getAssessmentTypeLabel(AssessmentType.Peer)} ${label("review", {
              capitalize: true,
            })}: `}
          </ComplianceProgramReportingDetailsLabel>
          <ComplianceProgramReportingAssessmentTypeDetails
            assessmentTemplate={peerAssessmentTemplate}
          />
        </div>
      )}

      {engagementSurveyTemplate && (
        <div className="flex items-center gap-2">
          <ComplianceProgramReportingDetailsLabel>
            {`${getAssessmentTypeLabel(AssessmentType.EngagementSurvey)}:`}
          </ComplianceProgramReportingDetailsLabel>
          <ComplianceProgramReportingAssessmentTypeDetails
            assessmentTemplate={engagementSurveyTemplate}
          />
        </div>
      )}

      <div className="flex items-start gap-2">
        <ComplianceProgramReportingDetailsLabel>
          Applies to:
        </ComplianceProgramReportingDetailsLabel>
        <div>
          {getAppliesToLabel(
            label,
            complianceProgram.appliesTo,
            complianceProgram.creator,
            currentUser.id,
          )}
          {complianceProgram.appliesTo ===
            ComplianceProgramAppliesTo.ReportsTo && (
            <span>
              {" "}
              <AppliesToUsersList users={complianceProgram.appliesToManagers} />
            </span>
          )}
          {complianceProgram.appliesTo === ComplianceProgramAppliesTo.Users && (
            <span>
              {" "}
              <AppliesToUsersList users={complianceProgram.appliesToUsers} />
            </span>
          )}
          {complianceProgram.appliesTo ===
            ComplianceProgramAppliesTo.Departments && (
            <span>
              {" "}
              (
              {complianceProgram.appliesToTeams.edges
                .map((edge) => edge?.node?.title)
                .join(", ")}
              )
            </span>
          )}
        </div>
      </div>
      {(excludedUsers.length > 0 ||
        complianceProgram.excludedHireDateAfter) && (
        <div className="flex items-center gap-2">
          <ComplianceProgramReportingDetailsLabel>
            Excluded:
          </ComplianceProgramReportingDetailsLabel>
          <div>
            {`${excludedUsers.map((user) => user.name).join(", ")}${
              complianceProgram.excludedHireDateAfter &&
              excludedUsers.length > 0
                ? ", "
                : ""
            }`}
            {complianceProgram.excludedHireDateAfter
              ? `Users hired after ${moment(
                  complianceProgram.excludedHireDateAfter,
                ).format("MMM D, YYYY")}`
              : ""}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceProgramReportingDetails;
